import { FC, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CardContent,
  FormControlLabel,
  LinearProgress,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { UPLOAD_FILE_MODAL } from "helpers/constants";
import { DragNDropFile } from "components/DragNDropFile";
import { MainLayoutContainer } from "components/Layout";
import CustomTable from "components/Table/CustomTable";
import { Link } from "react-router-dom";
import { ReportAlert } from "./components";
import { useUploadCSVUsers } from "./useUploadCSVUsers";
import { FieldWithEntitySelector } from "components/FieldWithEntitySelector";
import { CardOutline } from "components/Card";
import { FileDownload } from "@mui/icons-material";
import Markdown from "react-markdown";
import { MarkdownStyleContainer } from "components/Container/MarkdownStyleContainer";
import { useClinicText } from "hooks/useClinicText";
import * as React from "react";

type Props = {
  roles: string[];
};

export const UploadCSVUsers: FC<Props> = (props: Props) => {
  const {
    backRoute,
    loading,
    completed,
    displayData,
    data,
    selectedData,
    error,
    file,
    loadingData,
    report,
    organizations,
    organizationId,
    selectedOrganization,
    setSelectedOrganization,
    setFile,
    handleDownloadClick,
    handleOnComplete,
    handleContinue,
    setDisplayData,
    onSubmit,
  } = useUploadCSVUsers(props.roles);

  const { textGraduationClass } = useClinicText(false, selectedOrganization);
  const [sendInvite, setSendInvite] = useState(false);

  const columns =
    props.roles.length > 1
      ? [
          {
            id: "name",
            label: "Name",
            value: "name",
          },
          {
            id: "email",
            label: "Email",
            value: "email",
          },
          {
            id: "role",
            label: "Role",
            value: "role",
          },
        ]
      : [
          {
            id: "name",
            label: "Name",
            value: "name",
          },
          {
            id: "email",
            label: "Email",
            value: "email",
          },
        ];

  const handleChangeSendInvite = (event: any) => {
    setSendInvite(event.target.checked);
  };

  return (
    <MainLayoutContainer
      title={`Upload Csv Users ${organizationId ? `for ${textGraduationClass}` : ""}`}
      maxWidth={960}
    >
      <Box>
        {error && <Alert severity="error">{error}</Alert>}

        {!displayData && !loading && !completed && (
          <Box>
            <Box mt={2}>
              <DragNDropFile
                maxFileSizeMB={1}
                allowedExtensions={["csv"]}
                onFileUploaded={(file) => {
                  setFile(file);
                }}
              />
            </Box>
            <Box mt={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography component={"div"} variant="h6">
                  {UPLOAD_FILE_MODAL.TEMPLATE_EXAMPLE}
                </Typography>
                <Button
                  variant="text"
                  color="primary"
                  onClick={handleDownloadClick}
                  startIcon={<FileDownload />}
                  size="small"
                >
                  Download Template
                </Button>
              </Stack>
              <Box
                px={2}
                py={1}
                mt={1}
                sx={{
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                  border: "1px solid rgba(25, 118, 210, 0.3)",
                  borderRadius: 1,
                }}
              >
                <MarkdownStyleContainer>
                  <Markdown>
                    {props.roles.length > 1
                      ? UPLOAD_FILE_MODAL.TEMPLATE_EXAMPLE_DESCRIPTION_ADMIN
                      : UPLOAD_FILE_MODAL.TEMPLATE_EXAMPLE_DESCRIPTION}
                  </Markdown>
                </MarkdownStyleContainer>
              </Box>
            </Box>
          </Box>
        )}

        {displayData && !loading && !completed && (
          <Box mt={2}>
            {!organizationId && (
              <CardOutline>
                <CardContent>
                  <Typography variant="h6">Select Organization</Typography>
                  <Box mt={2}>
                    <FieldWithEntitySelector
                      entityList={organizations}
                      element={selectedOrganization}
                      onChangeElement={(selected) =>
                        setSelectedOrganization(selected)
                      }
                      entityName="Organizations"
                      // maxElements={1}
                      displayField="name"
                      idField="org_id"
                    />
                  </Box>
                </CardContent>
              </CardOutline>
            )}

            <FormControlLabel
              control={<Switch onChange={handleChangeSendInvite} />}
              label="Send Invite?"
            />

            <CustomTable
              entities={data}
              columns={columns}
              handleActionClick={() => {}}
              fieldToFilter={"name"}
              isSelectable={true}
              extraActionItems={[]}
              idColumn={"email"}
              entitiesIdsAlreadySelected={selectedData.current}
              onSelectionChanged={(selected) => {
                selectedData.current = selected;
              }}
            />
          </Box>
        )}

        <Box sx={{ mt: 5, display: "flex", justifyContent: "flex-end" }}>
          {!displayData && !loading && !completed && (
            <>
              <Box mr={2}>
                <Button variant="outlined" component={Link} to={backRoute}>
                  Cancel
                </Button>
              </Box>
              <Box mr={2}>
                <Button
                  disabled={!file}
                  onClick={() => handleContinue(file as File)}
                  variant="contained"
                  color="primary"
                >
                  Continue
                </Button>
              </Box>
            </>
          )}
          {displayData && !loading && !completed && (
            <>
              <Box mr={2}>
                <Button
                  variant="outlined"
                  onClick={() => setDisplayData(false)}
                >
                  Back
                </Button>
              </Box>
              <Box>
                <Button
                  disabled={loading}
                  onClick={() => onSubmit(sendInvite)}
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Submit
                </Button>
              </Box>
            </>
          )}
        </Box>
        {loading && (
          <Box mt={2} mb={4} maxWidth={720} sx={{ mx: "auto" }}>
            <Typography variant="body1" align="center">
              Uploading {~~((loadingData.current / loadingData.total) * 100)} %
            </Typography>
            <Box sx={{ width: "100%" }}>
              <LinearProgress
                variant="determinate"
                value={(loadingData.current / loadingData.total) * 100}
              />
            </Box>
          </Box>
        )}
        {completed && (
          <Box>
            <Stack spacing={2}>
              {report.success.length > 0 && (
                <ReportAlert
                  report={report.success}
                  severity="success"
                  textDescription="New users added successfully"
                />
              )}
              {report.alreadyExists.length > 0 && (
                <ReportAlert
                  report={report.alreadyExists}
                  severity="warning"
                  textDescription="Users already exists in the System"
                />
              )}
              {report.error.length > 0 && (
                <ReportAlert
                  report={report.error}
                  severity="error"
                  textDescription="Users couldn't created"
                />
              )}
            </Stack>
            <Box sx={{ mt: 5, display: "flex", justifyContent: "flex-end" }}>
              <Box>
                <Button
                  disabled={loading}
                  onClick={handleOnComplete}
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Complete
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </MainLayoutContainer>
  );
};
